import { createContext, ReactNode, useContext, useState } from "react"
import Modal from "customerAttachments/components/Modal"
import ModalProps from "customerAttachments/components/Modal/ModalProps"

type ModalType = Omit<ModalProps, "isOpen" | "setIsOpen">

interface ModalSetting extends ModalType {
  isOpen: boolean
}

interface ModalContextProps {
  setSetting: (props: ModalSetting) => void
  openModal: (props: ModalType) => void
}

const ModalContext = createContext<ModalContextProps | null>(null)

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [setting, setSetting] = useState<ModalSetting>({
    isOpen: false,
    title: null,
    content: null,
    onConfirm: undefined,
    onCancel: undefined,
    cancelButtonText: null,
    confirmButtonText: null,
    showConfirm: true,
    showCancel: true,
  })

  const value = {
    setting,
    setSetting,
    openModal: (props: ModalType) => {
      setSetting({
        ...props,
        isOpen: true,
      })
    },
  }

  const setIsOpen = (isOpen: boolean = false) => {
    setSetting((state) => {
      return {
        ...state,
        isOpen,
      }
    })
  }

  const { isOpen, content, ...restProps } = setting
  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal isOpen={isOpen} setIsOpen={setIsOpen} {...restProps}>
        {content}
      </Modal>
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  const context = useContext(ModalContext)

  if (!context) {
    throw new Error("useModal must be used within a ModalProvider")
  }
  return {
    openModal: (props: ModalType) => {
      context.setSetting({
        ...props,
        isOpen: true,
      })
    },
  }
}
